import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LeadService from '../../services/LeadService';

const SOURCE_TYPES = [
  { id: 1, name: 'Facebook',  icon: 'fa-facebook',  color: '#1877f2', bg: '#e7f0ff' },
  { id: 2, name: 'Instagram', icon: 'fa-instagram', color: '#e1306c', bg: '#fce4ec' },
  { id: 3, name: 'Reference', icon: 'fa-users',     color: '#2dce89', bg: '#e6faf3' },
  { id: 4, name: 'Google',    icon: 'fa-google',    color: '#ea4335', bg: '#fdecea' },
  { id: 5, name: 'WhatsApp',  icon: 'fa-whatsapp',  color: '#25d366', bg: '#e6faf0' },
  { id: 6, name: 'Website',   icon: 'fa-globe',     color: '#5e72e4', bg: '#eef0ff' },
];

const getSource = (id) => SOURCE_TYPES.find((s) => s.id === id) ?? { name: 'Unknown', icon: 'fa-question-circle', color: '#8392ab', bg: '#f4f6fb' };

const fmtDate = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  return isNaN(d.getTime()) || d.getFullYear() <= 1 ? '—'
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const initials = (first, last) => {
  const f = String(first ?? '');
  const l = String(last ?? '');
  return (`${(f[0] || '').toUpperCase()}${(l[0] || '').toUpperCase()}`) || '?';
};

const AVATAR_COLORS = [
  'linear-gradient(135deg,#5e72e4,#825ee4)',
  'linear-gradient(135deg,#2dce89,#11cdef)',
  'linear-gradient(135deg,#fb6340,#ffd600)',
  'linear-gradient(135deg,#f5365c,#fb6340)',
  'linear-gradient(135deg,#11cdef,#1171ef)',
];
const avatarColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

function InfoRow({ icon, label, value, color = '#5e72e4', href }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '12px 0', borderBottom: '1px solid #f0f2f5' }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: 14, flexShrink: 0 }}>
        <i className={`fas ${icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
        {href ? (
          <a href={href} style={{ fontSize: 14, color: '#344767', fontWeight: 500, wordBreak: 'break-word', textDecoration: 'none' }}>{value || '—'}</a>
        ) : (
          <p style={{ margin: 0, fontSize: 14, color: '#344767', fontWeight: 500, wordBreak: 'break-word' }}>{value || '—'}</p>
        )}
      </div>
    </div>
  );
}

function Card({ title, icon, iconColor = '#5e72e4', children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e9ecef', padding: '22px 24px', boxShadow: '0 2px 12px rgba(52,71,103,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1.5px solid #f4f6fb' }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: iconColor + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor, fontSize: 13 }}>
          <i className={`fas ${icon}`} />
        </div>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#344767', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</p>
      </div>
      {children}
    </div>
  );
}

function LeadDetails() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    LeadService.getLeadById(id)
      .then(setLead)
      .catch((e) => console.error('Error fetching lead details:', e))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid #f0f2f5', borderTop: '4px solid #5e72e4', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#8392ab', fontSize: 13, fontWeight: 600 }}>Loading lead profile…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!lead) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <i className="fas fa-user-slash" style={{ fontSize: 48, color: '#e9ecef', display: 'block', marginBottom: 12 }} />
        <p style={{ color: '#8392ab', fontWeight: 600 }}>Lead not found</p>
        <button onClick={() => navigate('/leads')} style={backBtnStyle}>Back to Leads</button>
      </div>
    );
  }

  const src       = getSource(lead.sourceTypeId);
  const fullName  = `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim() || 'Unknown Lead';
  const initStr   = initials(lead.firstName ?? '', lead.lastName ?? '');
  const bgGrad    = avatarColor(fullName);

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#f4f6fb' }}>
      <style>{`
        .ld-action-btn { border: none; border-radius: 10px; padding: 9px 18px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: opacity .15s, transform .15s; }
        .ld-action-btn:hover { opacity: 0.88; transform: translateY(-1px); }
      `}</style>

      {/* ── Back + breadcrumb ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="ld-action-btn" onClick={() => navigate(-1)} style={{ background: '#fff', color: '#344767', border: '1.5px solid #e9ecef' }}>
          <i className="fas fa-arrow-left" style={{ fontSize: 11 }} /> Back
        </button>
        <span style={{ fontSize: 12, color: '#8392ab' }}>Leads</span>
        <i className="fas fa-chevron-right" style={{ fontSize: 10, color: '#c0ccd8' }} />
        <span style={{ fontSize: 12, color: '#344767', fontWeight: 600 }}>{fullName}</span>
      </div>

      {/* ── Hero banner ── */}
      <div style={{ background: 'linear-gradient(135deg,#344767 0%,#5e72e4 100%)', borderRadius: 20, padding: '28px 32px', marginBottom: 24, color: '#fff', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', boxShadow: '0 8px 30px rgba(94,114,228,0.25)' }}>
        {/* Avatar */}
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: bgGrad, border: '3px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#fff', flexShrink: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
          {initStr}
        </div>

        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <h4 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: '#fff' }}>{fullName}</h4>
            <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em' }}>
              {lead.leadCode}
            </span>
            {lead.isLeadAssigned && (
              <span style={{ background: '#2dce8940', color: '#2dce89', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>
                <i className="fas fa-check-circle" style={{ marginRight: 4 }} />Assigned
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {lead.email && (
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-envelope" style={{ fontSize: 11 }} />{lead.email}
              </span>
            )}
            {lead.contactNumber && (
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-phone" style={{ fontSize: 11 }} />{lead.contactNumber}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {lead.contactNumber && (
            <a href={`tel:${lead.contactNumber}`} className="ld-action-btn" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', textDecoration: 'none', backdropFilter: 'blur(4px)' }}>
              <i className="fas fa-phone" /> Call
            </a>
          )}
          {lead.contactNumber && (
            <a href={`https://wa.me/${lead.contactNumber}`} target="_blank" rel="noreferrer" className="ld-action-btn" style={{ background: '#25d36640', color: '#fff', textDecoration: 'none' }}>
              <i className="fab fa-whatsapp" /> WhatsApp
            </a>
          )}
          {lead.email && (
            <a href={`mailto:${lead.email}`} className="ld-action-btn" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', textDecoration: 'none' }}>
              <i className="fas fa-envelope" /> Email
            </a>
          )}
          <button className="ld-action-btn" onClick={() => navigate(`/editlead/${lead.id}`)} style={{ background: '#fff', color: '#5e72e4' }}>
            <i className="fas fa-edit" /> Edit
          </button>
        </div>
      </div>

      {/* ── Source banner ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#fff', borderRadius: 14, border: '1px solid #e9ecef', padding: '14px 20px', marginBottom: 24, boxShadow: '0 2px 8px rgba(52,71,103,0.05)' }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: src.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: src.color, fontSize: 18, flexShrink: 0 }}>
          <i className={`fab ${src.icon}`} />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead Source</p>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: src.color }}>{src.name}</p>
        </div>
        {lead.source && (
          <>
            <div style={{ width: 1, height: 36, background: '#e9ecef', margin: '0 8px' }} />
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Campaign / Channel</p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#344767' }}>{lead.source}</p>
            </div>
          </>
        )}
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead Since</p>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#344767' }}>{fmtDate(lead.startDate)}</p>
        </div>
      </div>

      {/* ── Two-column cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>

        {/* Contact Details */}
        <Card title="Contact Details" icon="fa-address-card" iconColor="#5e72e4">
          <InfoRow icon="fa-user"        label="Full Name"      value={fullName}             color="#5e72e4" />
          <InfoRow icon="fa-envelope"    label="Email"          value={lead.email}           color="#11cdef" href={lead.email ? `mailto:${lead.email}` : undefined} />
          <InfoRow icon="fa-phone"       label="Contact Number" value={lead.contactNumber}   color="#2dce89" href={lead.contactNumber ? `tel:${lead.contactNumber}` : undefined} />
          <InfoRow icon="fa-map-marker-alt" label="Address"     value={lead.address}         color="#fb6340" />
        </Card>

        {/* Lead Info */}
        <Card title="Lead Information" icon="fa-info-circle" iconColor="#2dce89">
          <InfoRow icon="fa-hashtag"      label="Lead Code"     value={lead.leadCode}        color="#344767" />
          <InfoRow icon="fa-building"     label="Property"      value={lead.property}        color="#5e72e4" />
          <InfoRow icon="fa-calendar-alt" label="Start Date"    value={fmtDate(lead.startDate)} color="#fb6340" />
          <InfoRow icon="fa-calendar-check" label="End Date"    value={fmtDate(lead.endDate)}   color="#f5365c" />
          <InfoRow icon="fa-user-tie"     label="Assigned To"   value={lead.user ? `${lead.user.firstName || ''} ${lead.user.lastName || ''}`.trim() : '—'} color="#825ee4" />
        </Card>
      </div>
    </div>
  );
}

const backBtnStyle = {
  marginTop: 16,
  padding: '9px 20px',
  background: '#5e72e4',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

export default LeadDetails;
